// Port of ios/Sources/Calls/CallsCodec.swift (Android `CallsCodec`, line for line, pinned to the
// Calls plugin v1.12.1 sources).
//
// The Calls signaling wire format, as pure functions — every frame this client sends and every
// event it reads, with no socket attached, so the whole protocol layer is exercised by unit tests
// (`callsCodec.test.ts`, the port of `CallsCodecTests`) rather than discovered call-by-call.
//
// Signaling rides a SECOND, dedicated Mattermost websocket whose connection id IS the call session
// identity. Messages are standard MM websocket requests with the action prefixed
// `custom_com.mattermost.calls_`; everything is JSON text EXCEPT the SDP frame, which must be
// binary MessagePack because its payload is zlib-compressed bytes and JSON cannot carry bytes.
// Server-to-client signaling arrives as the `_signal` event whose `data.data` is a JSON string of
// {type: answer|offer|candidate, ...}.
//
// The zlib trap (docs/PARITY.md "Calls"): the plugin and rtcd speak zlib-WRAPPED deflate
// (RFC 1950). Apple's Compression framework only does RAW deflate, so Swift adds the 2-byte header
// and Adler-32 trailer by hand. The browser's `CompressionStream('deflate')` IS RFC 1950 already —
// the envelope must NOT be added again here (and 'deflate-raw' would be the wrong format).
import { EmojiCharacters } from './emojiCharacters';
import { decodeAll, MsgPackWriter } from './callsMsgPack';

export const CALLS_EVENT_PREFIX = 'custom_com.mattermost.calls_';

// MARK: - Emoji

/**
 * One in-call reaction, in the plugin's `EmojiData` shape (`name`, `skin`, `unified`, `literal`).
 * The webapp DRAWS a reaction by looking `unified` up, lowercased, in its emoji table — so `unified`
 * must be the lowercase code points joined by "-", variation selector included (heart is
 * "2764-fe0f"), or a web user sees a blank square.
 */
export interface CallEmoji {
  name: string;
  unified: string;
  literal: string;
}

export const CallEmoji = {
  /** The webapp's own quick row (components/expanded_view/reaction_button.tsx), same order. */
  quickNames: ['+1', 'clap', 'joy', 'heart', 'tada'] as const,

  /** From a shortcode this app knows a glyph for. */
  fromName(name: string): CallEmoji | null {
    const glyph = EmojiCharacters.character(name);
    if (!glyph) return null;
    return { name, unified: CallEmoji.unifiedOf(glyph), literal: glyph };
  },

  fromWire(wire: unknown): CallEmoji | null {
    const w = (wire && typeof wire === 'object' ? wire : {}) as Record<string, unknown>;
    const name = typeof w.name === 'string' ? w.name : '';
    const unified = (typeof w.unified === 'string' ? w.unified : '').toLowerCase();
    const literal = typeof w.literal === 'string' ? w.literal : '';
    if (!name && !unified && !literal) return null;
    return { name, unified, literal };
  },

  wireObject(emoji: CallEmoji): Record<string, string> {
    return { name: emoji.name, skin: '', unified: emoji.unified, literal: emoji.literal };
  },

  /** What to draw: the literal if the sender gave one, else the code points, else our own table. */
  glyph(emoji: CallEmoji): string {
    if (emoji.literal) return emoji.literal;
    const points = emoji.unified
      .split('-')
      .map((h) => parseInt(h, 16))
      .filter((n) => Number.isFinite(n) && n > 0 && n <= 0x10ffff);
    if (points.length) return String.fromCodePoint(...points);
    return EmojiCharacters.character(emoji.name) ?? `:${emoji.name}:`;
  },

  unifiedOf(glyph: string): string {
    return [...glyph].map((c) => c.codePointAt(0)!.toString(16)).join('-');
  },
};

// MARK: - Frames this client sends

function frame(action: string, seq: number, data?: Record<string, unknown>): string {
  const json: Record<string, unknown> = { action, seq };
  if (data) json.data = data;
  return JSON.stringify(json);
}

export const CallsFrames = {
  authChallenge: (seq: number, token: string) => frame('authentication_challenge', seq, { token }),
  ping: (seq: number) => JSON.stringify({ action: 'ping', seq }),

  /**
   * dcSignaling explicitly declined: SDP over the data channel is an optimization with its own
   * failure modes, and the websocket path is the one every server version supports.
   */
  join(seq: number, channelId: string, threadId?: string | null): string {
    const data: Record<string, unknown> = { channelID: channelId, dcSignaling: false, av1Support: false };
    if (threadId) data.threadID = threadId;
    return frame(CALLS_EVENT_PREFIX + 'join', seq, data);
  },

  reconnect: (seq: number, channelId: string, originalConnId: string, prevConnId: string) =>
    frame(CALLS_EVENT_PREFIX + 'reconnect', seq, { channelID: channelId, originalConnID: originalConnId, prevConnID: prevConnId }),

  leave: (seq: number) => frame(CALLS_EVENT_PREFIX + 'leave', seq),
  mute: (seq: number) => frame(CALLS_EVENT_PREFIX + 'mute', seq),
  unmute: (seq: number) => frame(CALLS_EVENT_PREFIX + 'unmute', seq),
  screenOff: (seq: number) => frame(CALLS_EVENT_PREFIX + 'screen_off', seq),

  /**
   * The stream id announces which incoming stream is the screen BEFORE its track arrives. The inner
   * payload is a JSON STRING — server/websocket.go reads data["data"] as a string and re-parses it;
   * a nested object would be silently dropped.
   */
  screenOn: (seq: number, screenStreamId: string) =>
    frame(CALLS_EVENT_PREFIX + 'screen_on', seq, { data: JSON.stringify({ screenStreamID: screenStreamId }) }),

  /** The camera twin of `screenOn`: announced BEFORE the track, or rtcd drops it as "unexpected". */
  videoOn: (seq: number, videoStreamId: string) =>
    frame(CALLS_EVENT_PREFIX + 'video_on', seq, { data: JSON.stringify({ videoStreamID: videoStreamId }) }),

  videoOff: (seq: number) => frame(CALLS_EVENT_PREFIX + 'video_off', seq),
  raiseHand: (seq: number) => frame(CALLS_EVENT_PREFIX + 'raise_hand', seq),
  unraiseHand: (seq: number) => frame(CALLS_EVENT_PREFIX + 'unraise_hand', seq),

  /** An in-call emoji reaction; the plugin re-parses `data.data` as `EmojiData` JSON. */
  react: (seq: number, emoji: CallEmoji) => frame(CALLS_EVENT_PREFIX + 'react', seq, { data: JSON.stringify(CallEmoji.wireObject(emoji)) }),

  /** ICE candidates are small and textual, so they stay on the JSON path. */
  ice: (seq: number, candidateJson: string) => frame(CALLS_EVENT_PREFIX + 'ice', seq, { data: candidateJson }),

  /** The one binary frame: {action, seq, data: {data: <zlib(JSON(sdp))>}} as MessagePack. */
  async sdp(seq: number, sdpJson: string): Promise<Uint8Array> {
    const compressed = await deflate(new TextEncoder().encode(sdpJson));
    return new MsgPackWriter()
      .packMapHeader(3)
      .packString('action')
      .packString(CALLS_EVENT_PREFIX + 'sdp')
      .packString('seq')
      .packInt(seq)
      .packString('data')
      .packMapHeader(1)
      .packString('data')
      .packBinary(compressed)
      .toBytes();
  },
};

// MARK: - Events this client reads

export type CallSocketEvent =
  /** `hello` — the server assigned this connection its id, the call session identity. */
  | { kind: 'hello'; connectionId: string }
  /** `_join` addressed to our connection — the server accepted us into the call. */
  | { kind: 'joinAck' }
  /** `_error` addressed to our connection — usually a rejected join, with the reason. */
  | { kind: 'error'; message: string }
  /** `_signal` — an SDP answer/offer or a remote ICE candidate, as raw JSON. */
  | { kind: 'signal'; json: string }
  /** `_call_state` — the authoritative roster snapshot sent right after joining. */
  | { kind: 'callState'; channelId: string; callJson: string }
  | { kind: 'pong'; seqReply: number }
  | { kind: 'userJoined'; userId: string; sessionId: string }
  | { kind: 'userLeft'; userId: string; sessionId: string }
  | { kind: 'userMuted'; userId: string; sessionId: string; muted: boolean }
  | { kind: 'userSpeaking'; userId: string; sessionId: string; speaking: boolean }
  | { kind: 'screenState'; sessionId: string; on: boolean }
  /** `raisedAt` is the server's epoch millis; 0 = lowered. */
  | { kind: 'handState'; userId: string; sessionId: string; raisedAt: number }
  | { kind: 'videoState'; userId: string; sessionId: string; on: boolean }
  | { kind: 'reacted'; userId: string; sessionId: string; emoji: CallEmoji; at: number }
  | { kind: 'callEnd' }
  | { kind: 'ignored' };

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function parseJson(text: string): Record<string, unknown> | null {
  try {
    const value = JSON.parse(text);
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

/** The plugin uses `userID` on mute/voice/screen events and `user_id` on join/left. */
function userIdOf(data: Record<string, unknown>): string {
  const camel = typeof data.userID === 'string' ? data.userID : '';
  return camel || (typeof data.user_id === 'string' ? data.user_id : '');
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Parses one text message off the call socket. `ownConnectionId` scopes the events addressed to a
 * specific connection (join ack, error, signal): the server also broadcasts other sessions' events
 * to the channel, and acting on an ack meant for someone else would start this client's media dance
 * on a call it was never admitted to.
 */
export function parseSocketMessage(text: string, ownConnectionId: string | null): CallSocketEvent {
  const json = parseJson(text);
  if (!json) return { kind: 'ignored' };
  if (typeof json.seq_reply === 'number' && json.seq_reply >= 0) return { kind: 'pong', seqReply: json.seq_reply };

  const event = typeof json.event === 'string' ? json.event : '';
  const data = asObject(json.data);
  if (event === 'hello') {
    const connectionId = typeof data.connection_id === 'string' ? data.connection_id : '';
    return connectionId ? { kind: 'hello', connectionId } : { kind: 'ignored' };
  }
  if (!event.startsWith(CALLS_EVENT_PREFIX)) return { kind: 'ignored' };

  const addressed = ownConnectionId !== null && data.connID === ownConnectionId;
  const str = (key: string) => (typeof data[key] === 'string' ? (data[key] as string) : '');
  const sessionId = str('session_id');
  switch (event.slice(CALLS_EVENT_PREFIX.length)) {
    case 'join':
      return addressed ? { kind: 'joinAck' } : { kind: 'ignored' };
    case 'error':
      return addressed ? { kind: 'error', message: str('data') } : { kind: 'ignored' };
    case 'signal':
      return addressed ? { kind: 'signal', json: str('data') } : { kind: 'ignored' };
    case 'call_state':
      return { kind: 'callState', channelId: str('channel_id'), callJson: typeof data.call === 'string' ? data.call : data.call ? JSON.stringify(data.call) : '' };
    case 'user_joined':
      return { kind: 'userJoined', userId: userIdOf(data), sessionId };
    case 'user_left':
      return { kind: 'userLeft', userId: userIdOf(data), sessionId };
    case 'user_muted':
      return { kind: 'userMuted', userId: userIdOf(data), sessionId, muted: true };
    case 'user_unmuted':
      return { kind: 'userMuted', userId: userIdOf(data), sessionId, muted: false };
    case 'user_voice_on':
      return { kind: 'userSpeaking', userId: userIdOf(data), sessionId, speaking: true };
    case 'user_voice_off':
      return { kind: 'userSpeaking', userId: userIdOf(data), sessionId, speaking: false };
    case 'user_screen_on':
      return { kind: 'screenState', sessionId, on: true };
    case 'user_screen_off':
      return { kind: 'screenState', sessionId, on: false };
    case 'user_raise_hand':
    case 'user_unraise_hand':
      return { kind: 'handState', userId: userIdOf(data), sessionId, raisedAt: num(data.raised_hand) };
    case 'user_video_on':
      return { kind: 'videoState', userId: userIdOf(data), sessionId, on: true };
    case 'user_video_off':
      return { kind: 'videoState', userId: userIdOf(data), sessionId, on: false };
    case 'user_reacted': {
      const emoji = CallEmoji.fromWire(data.emoji);
      if (!emoji) return { kind: 'ignored' };
      return { kind: 'reacted', userId: userIdOf(data), sessionId, emoji, at: num(data.timestamp) };
    }
    case 'call_end':
      return { kind: 'callEnd' };
    default:
      return { kind: 'ignored' };
  }
}

/**
 * The channel a plugin event was broadcast to, or null when it was addressed otherwise. `user_joined`
 * / `user_left` go to EVERY member of the call's channel, so while on one call this socket also
 * hears people joining a call somewhere else — and folding those into the roster added ghosts.
 */
export function broadcastChannelId(text: string): string | null {
  const json = parseJson(text);
  const channelId = asObject(json?.broadcast).channel_id;
  return typeof channelId === 'string' && channelId ? channelId : null;
}

/**
 * What a REMOTE track is and whose it is, from its id. rtcd names every track it forwards
 * `<type>_<senderSessionId>_<8 random>` (utils.go `genTrackID`). The media map's `sender_id` cannot
 * say whose it is: rtcd fills it with the RECEIVING session's own id.
 */
export function remoteTrackInfo(trackId: string): { type: string; sessionId: string } | null {
  const fields = trackId.split('_');
  if (fields.length !== 3 || !fields[0] || !fields[1]) return null;
  return { type: fields[0], sessionId: fields[1] };
}

// MARK: - Data-channel messages

/** calls-common `dc_msg.js` types this client speaks. */
export const DcType = { ping: 1, pong: 2, sdp: 3, lock: 7, unlock: 8, mediaMap: 9 } as const;

export type DcMessage =
  | { kind: 'pong' }
  | { kind: 'sdp'; json: string }
  | { kind: 'lockResponse'; acquired: boolean }
  /** mid → track type ("voice" | "screen" | "screen-audio" | "video"), and mid → `sender_id`
   *  (which rtcd fills with the RECEIVER's own session — ownership comes from the track id). */
  | { kind: 'mediaMap'; typesByMid: Record<string, string>; sendersByMid: Record<string, string> }
  | { kind: 'ignored' };

export const encodeDcPing = () => new MsgPackWriter().packInt(DcType.ping).toBytes();
export const encodeDcLock = () => new MsgPackWriter().packInt(DcType.lock).toBytes();
export const encodeDcUnlock = () => new MsgPackWriter().packInt(DcType.unlock).toBytes();

export async function decodeDcMessage(data: Uint8Array): Promise<DcMessage> {
  let values;
  try {
    values = decodeAll(data);
  } catch {
    return { kind: 'ignored' };
  }
  const type = values[0];
  if (typeof type !== 'number') return { kind: 'ignored' };
  switch (type) {
    case DcType.pong:
      return { kind: 'pong' };
    case DcType.sdp: {
      const payload = values[1];
      if (!(payload instanceof Uint8Array)) return { kind: 'ignored' };
      try {
        return { kind: 'sdp', json: new TextDecoder().decode(await inflate(payload)) };
      } catch {
        return { kind: 'ignored' };
      }
    }
    case DcType.lock:
      return { kind: 'lockResponse', acquired: values.length > 1 && values[1] === true };
    case DcType.mediaMap: {
      const map = values[1];
      if (!map || typeof map !== 'object' || Array.isArray(map) || map instanceof Uint8Array) return { kind: 'ignored' };
      const typesByMid: Record<string, string> = {};
      const sendersByMid: Record<string, string> = {};
      for (const [mid, value] of Object.entries(map)) {
        const info = asObject(value);
        if (typeof info.type !== 'string') continue;
        typesByMid[mid] = info.type;
        if (typeof info.sender_id === 'string' && info.sender_id) sendersByMid[mid] = info.sender_id;
      }
      return { kind: 'mediaMap', typesByMid, sendersByMid };
    }
    default:
      return { kind: 'ignored' };
  }
}

// MARK: - Compression (RFC 1950 zlib, natively — see the header note)

async function pipeThrough(data: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const body = new Blob([data as BlobPart]).stream().pipeThrough(stream as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  return new Uint8Array(await new Response(body).arrayBuffer());
}

/** zlib-WRAPPED deflate (RFC 1950) — what Go's `compress/zlib` and Java's `Deflater` speak. */
export function deflate(data: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(data, new CompressionStream('deflate'));
}

/** True when the two bytes are a valid zlib header (CM = 8, FCHECK makes CMF·FLG a multiple of 31). */
function hasZlibHeader(data: Uint8Array): boolean {
  return data.length >= 6 && (data[0] & 0x0f) === 8 && ((data[0] << 8) | data[1]) % 31 === 0;
}

/** Inflates a zlib stream; a RAW deflate body (no envelope) is accepted too, as Apple does. */
export function inflate(data: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(data, new DecompressionStream(hasZlibHeader(data) ? 'deflate' : 'deflate-raw'));
}
